import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // The `autoplay` attribute alone is unreliable once a video's <source>
    // elements are attached dynamically (as above) rather than present when
    // the browser first parses the tag — many mobile engines (iOS Safari,
    // several Android WebViews) only honour autoplay on a native gesture-free
    // play() call, not on the attribute, after a dynamic src swap. The video
    // is always muted+playsinline, which every major engine exempts from its
    // autoplay-blocking policy, so play() should succeed; the retries across
    // both media-readiness events cover engines with different timing for
    // when a freshly-loaded video actually becomes playable.
    const tryPlay = () => {
      videoElement.play().catch(() => {});
    };
    tryPlay();
    videoElement.addEventListener('loadeddata', tryPlay, { once: true });
    videoElement.addEventListener('canplay', tryPlay, { once: true });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}

/**
 * Reinforces autoplay for the hero section's video, which Shopify's video_tag
 * filter server-renders with autoplay/muted/playsinline all present from the
 * initial parse -- so this isn't the dynamic-src-swap situation the custom
 * element above exists for. It lives in this file, not a {% javascript %}
 * block in sections/hero.liquid, because Shopify's section-script compiler
 * does not pick up a newly-added javascript block on a stock theme section
 * (confirmed: it never appears in the compiled sections bundle, unlike the
 * identical pattern on this theme's custom sections). This file is loaded
 * unconditionally on every page via snippets/scripts.liquid, so it reaches
 * the browser regardless.
 */
function playHeroVideos() {
  document.querySelectorAll('video.hero__media').forEach((video) => {
    const tryPlay = () => {
      video.play().catch(() => {});
    };
    tryPlay();
    video.addEventListener('loadeddata', tryPlay, { once: true });
    video.addEventListener('canplay', tryPlay, { once: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', playHeroVideos, { once: true });
} else {
  playHeroVideos();
}
